/* @ts-ignore */
import React, { useEffect, useRef, useState } from 'react'

import SwiperComponent from 'react-id-swiper'
import resolveProps, { resolveFns } from '../../../utils/resolveProps'
import useValidations from '../../../hooks/use-validation'
import { TabSelectProps } from './types'

import './index.scss'

const config = {
  types: ['inverse'],
  modifiers: ['fluid'],
  states: []
}

const KEY_ENTER = 13
const KEY_SPACE = 32
const KEY_TAB = 9
const KEY_LEFT = 37
const KEY_RIGHT = 39

const params = {
  ContainerEl: 'div',
  containerClass: 'mdn-Swiper mdn-Steps-swiper',
  WrapperEl: 'ul',
  rebuildOnUpdate: true,
  wrapperClass: 'mdn-TabSelect-list mdn-Swiper-wrapper',
  slideClass: 'mdn-TabSelect-item',
  slidesPerView: 'auto',
  pagination: {},
  navigation: {},
  grabCursor: true
}

export default function TabSelect(props: TabSelectProps) {
  const {
    inverse,
    data,
    dataTabSelect,
    swiperConfigs,
    onClick,
    onTabChange = () => {},
    children,
    gtm,
    currentTab,
    ...rest
  } = props

  const tabsRef = React.useRef(null)
  const contentRef = React.useRef(null)
  const tabsId = `tab__${
    new Date()
      .valueOf()
      .toString()
      .match(/\d{6}$/)[0]
  }`
  const { shallowEqualObj } = useValidations()
  const items = data || dataTabSelect

  const isFluid = (items.length < 4)

  const currentIndex = [...items]?.findIndex(
    // @ts-ignore
    (item) => item?.isActive === true || item?.props?.isActive === true
  )

  const initialTab = currentIndex === -1 ? 0 : currentIndex

  const [tabs, setTabs] = React.useState({
    currentTab: initialTab,
    items: items
  })

  const [lastElement, setLastElement] = useState<React.ReactNode>()

  React.useEffect(() => {
    if (!shallowEqualObj(tabs.items, data)) {
      const currentPos =
        tabs.currentTab >= data.length ? data.length - 1 : tabs.currentTab
      setTabs({ currentTab: currentPos, items: data })
    }
  }, [data])

  const [isTabIndexPaused, setPausedTabIndex] = React.useState(false)

  const handleOnclick = React.useCallback((index, isVirtual) => {
    tabsRef.current.swiper.slideTo(tabs.currentTab, 0, false)
    setTabs((state) => {
      let toUpdate = { ...state, currentTab: index }
      onTabChange(toUpdate)
      return toUpdate
    })

    !!onClick && !isVirtual && onClick(tabsRef.current)
  }, [])

  React.useEffect(() => {
    if (!!currentTab) {
      handleOnclick(currentTab, true)
    }
  }, [currentTab])

  const handleKeyDown = (e) => {
    if(e.keyCode !== KEY_TAB) return;

    // Contexto apenas TAB
    if (!e.shiftKey) {
      if (e.target.ariaExpanded === 'true') {
        // se o atual for uma área expandida, deve entrar.
        let elementToFocus = contentRef.current.querySelector(
          '[aria-hidden="false"]'
        )
        setLastElement(lastElementFromPanel)
        e.preventDefault()
        elementToFocus.tabIndex = 0
        elementToFocus.focus({
          focusVisible: true
        })
        removeTabIndexesInsidePanel() // É necessário pra quando o usuário navegar por toda a página e voltar pro tabselect novamente.
        elementToFocus.tabIndex = null
      } else {
        if (isContentLastElement(e.target)) {
          let elementToFocus = tabsRef.current.querySelectorAll(
            '.mdn-TabSelect-anchor'
          )[tabs.currentTab + 1]
          if (elementToFocus) {
            e.preventDefault()
            elementToFocus.focus({
              focusVisible: true
            })
          }
        } else if (isLastTab(e.target)) {
          skipTabAndItems(
            contentRef.current.querySelector('[aria-hidden="false"]')
          )
        }
      }
    }
    /* else { // Contexto apenas SHIFT + TAB (ainda nao se mostrou necessário)
    }*/
  }

  const handleTabKeyDown = (e) => {
    //if(e.keyCode !== KEY_TAB) return;

    if (e.keyCode == KEY_LEFT) {
      if (isFirstTab(e.target)) {
        skipTabAndItems(
          contentRef.current.querySelector('[aria-hidden="false"]')
        )
        return;
      }
      handleOnclick(tabs.currentTab -1, true)
      let elementToFocus = tabsRef.current.querySelectorAll(
        '.mdn-TabSelect-anchor'
      )[tabs.currentTab - 1]
      if (elementToFocus) {
        e.preventDefault()
        elementToFocus.focus({
          focusVisible: true
        })
      }
      return;
    }
    if (e.keyCode == KEY_RIGHT) {
      if (isLastTab(e.target)) {
        skipTabAndItems(
          contentRef.current.querySelector('[aria-hidden="false"]')
        )
        return
      }
      handleOnclick(tabs.currentTab + 1, true)
      let elementToFocus = tabsRef.current.querySelectorAll(
        '.mdn-TabSelect-anchor'
      )[tabs.currentTab + 1]
      if (elementToFocus) {
        e.preventDefault()
        elementToFocus.focus({
          focusVisible: true
        })
      }
      return;
    }
  }

  const skipTabAndItems = (parent) => {
    parent.tabIndex = -1
    let elements = parent.querySelectorAll('*')
    for (let i = 0, lgt = elements.length; i < lgt; i++) {
      elements[i].tabIndex = -1
    }
  }

  const clearTabSelectTabIndex = () => {
    let itensToClear = contentRef.current?.querySelectorAll('[tabindex]')
    for (let i = 0, lgt = itensToClear.length; i < lgt; i++) {
      itensToClear[i].tabIndex = null
    }
  }

  const isLastTab = (element) => {
    return (
      tabsRef.current.contains(element) &&
      element.parentNode.nextSibling === null
    )
  }

  const isFirstTab = (element) => {
    return (
      tabsRef.current.contains(element) &&
      element.parentNode.previousSibling === null
    )
  }

  const isContentLastElement = (element) => {
    let isThePanel = element.classList.contains('mdn-TabSelect-panel')
    return (
      (isThePanel && !hasSomeFocusableChild(element)) ||
      (!isThePanel && lastElement === element)
    )
  }

  const lastElementFromPanel = () => {
    let panel = contentRef.current.querySelectorAll('[aria-hidden="false"]')[0]
    let e = panel.querySelectorAll(`
    input:not([disabled]),
    select:not([disabled]),
    textarea:not([disabled]),
    button:not([disabled]),
    a,
    [tab-index]
  `)
    return e[e.length - 1]
  }

  const removeTabIndexesInsidePanel = () => {
    let panel = contentRef.current.querySelectorAll('[aria-hidden="false"]')[0]
    let e = panel.querySelectorAll('*')
    for (let i = 0, lgt = e.length; i < lgt; i++) {
      e[i].removeAttribute('tabindex')
    }
  }

  const hasSomeFocusableChild = (element) => {
    return element.querySelectorAll(`
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      button:not([disabled]),
      a,
      [tab-index]
    `).length
  }

  useEffect(() => {}, [isTabIndexPaused])

  return (
    <div
      className={`${resolveProps(props, config, 'TabSelect')} `}
      {...rest}
      onKeyDown={handleKeyDown}
    >
      <SwiperComponent
        {...params}
        // @ts-ignore
        activeSlideKey={`${tabs.currentTab}`}
        ref={tabsRef}
        // @ts-ignore
        a11y={false}
        threshold={10}
      >
        {tabs.items.map((item, index) => {
          // @ts-ignore
          const { gtm = {}, title, onClick = () => {}, id, titleTag } = item
          const { gtmClassName = '', dataGTM = {} } = gtm

          return (
            <li
              key={index}
              className={`${
                tabs.currentTab === index ? 'mdn-TabSelect-active' : ''
              } `}
              aria-owns={`item-tab-${tabsId}-${index}`}
              aria-controls={`item-tab-${tabsId}-${index}`}
              onBlur={clearTabSelectTabIndex}
            >
              <button
                // tabIndex={ !isTabIndexPaused ? (index*2)+1 : null}
                // onClick={handleClick}
                {...dataGTM}
                // @ts-ignore
                onClick={resolveFns(() => handleOnclick(index), (e) => onClick(e, index))}
                type="button"
                id={id}
                aria-expanded={tabs.currentTab === index}
                className={`mdn-TabSelect-anchor ${gtmClassName}`}
                onKeyDown={handleTabKeyDown}
              >
                {titleTag ? React.createElement(titleTag, null, title) : title}
              </button>
            </li>
          )
        })}
      </SwiperComponent>
      {/* <div className="mdn-TabSelect-content" onFocus={handleContentFocus}> */}
      <div
        className="mdn-TabSelect-content"
        ref={contentRef}
        onBlur={clearTabSelectTabIndex}
      >
        {tabs?.items?.map((item, index) => {
          // if(tabs.currentTab === index) {
          //   let length = items[index]?.content.props.children.length

          //   // .props.onBlur = (e) => {
          //   //   setPausedTabIndex(false)
          //   // }
          // }

          return (
            <div
              key={`tab_${index}`}
              id={`item-tab-${tabsId}-${index}`}
              // tabIndex={ !isTabIndexPaused ? (index*2)+2 : null}
              aria-hidden={tabs.currentTab !== index}
              style={{
                display: `${tabs.currentTab === index ? 'flex' : 'none'}`
              }}
              className="mdn-TabSelect-panel mdn-TabSelect-active"
            >
              {tabs.currentTab === index
                ? // @ts-ignore
                  items[index]?.content
                : null}

              {/* <button onFocus={()=>{
                setPausedTabIndex(false)
                tabsRef.current.querySelectorAll('.mdn-TabSelect-list button')[tabs.currentTab+1].focus();
                setPausedBefore(false)
              }}>ultimo forçado</button> */}
            </div>
          )
        })}
      </div>
    </div>
  )
}
